import type { ReportingTranscriptItem } from "@/types/reporting-agent";

/** One content block of a persisted message (Anthropic-style). `text` blocks
 *  render as bubbles; `tool_use`/`tool_result` blocks are dropped from the
 *  visible text, but three tool interactions are reconstructed from them:
 *  dashboard builds (to restore the "view dashboard" link), and the `ask_user` /
 *  `request_approval` boxes (to restore the question/plan inline). */
interface RawBlock {
  type?: string;
  text?: string;
  id?: string;
  name?: string;
  input?: Record<string, unknown>;
  tool_use_id?: string;
  content?: unknown;
  is_error?: boolean;
}

interface RawMessage {
  role?: string;
  content?: RawBlock[] | string;
}

// Tools that produce a dashboard artifact.
const DASHBOARD_TOOLS = new Set(["build_dashboard", "edit_dashboard"]);
const ASK_USER = "ask_user";
const REQUEST_APPROVAL = "request_approval";
// save_plan is gated by the registry's enforced approval (requires_approval), so
// on reload the plan box is reconstructed from the save_plan tool_use + result —
// there is no request_approval block for it. (approval_summary/body live in the
// backend save_plan tool; mirrored here since the transcript stores only params.)
const SAVE_PLAN = "save_plan";
const INTERACTION_TOOLS = new Set([ASK_USER, REQUEST_APPROVAL, SAVE_PLAN]);

// The saved artifact id appears only as prose in the tool_result content, e.g.
// "…; saved (<uuid>)." / "…; saved as a new version (<uuid>).". Coupled to that
// wording by necessity (the transcript carries no structured artifact ref); a
// non-match just means no link is restored — never a crash.
const ARTIFACT_RE = /\(([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})\)/i;

// A denied request_approval result is "REJECTED: <note>" (or "REJECTED: no reason").
const REJECTED_PREFIX = "REJECTED: ";

const str = (v: unknown): string | null => (typeof v === "string" ? v : null);

/** The interaction fields recovered from a pending `ask_user`/`request_approval`
 *  tool_use, held until its tool_result arrives with the answer/decision. */
interface PendingInteraction {
  name: string;
  toolCallId: string;
  input: Record<string, unknown>;
}

function inputRequestItem(pending: PendingInteraction, result: RawBlock): ReportingTranscriptItem {
  const options = pending.input.options;
  // is_error ⇒ the tool timed out with no reply — leave it unanswered.
  const answer = result.is_error ? null : str(result.content);
  return {
    kind: "input_request",
    id: crypto.randomUUID(),
    toolCallId: pending.toolCallId,
    prompt: str(pending.input.question) ?? "",
    options: Array.isArray(options)
      ? (options.filter((o) => typeof o === "string") as string[])
      : null,
    answer,
  };
}

function approvalRequestItem(
  pending: PendingInteraction,
  result: RawBlock,
): ReportingTranscriptItem {
  const content = str(result.content) ?? "";
  let approved: boolean | null = null;
  let note: string | null = null;
  if (content === "APPROVED") {
    approved = true;
  } else if (content.startsWith(REJECTED_PREFIX)) {
    approved = false;
    const reason = content.slice(REJECTED_PREFIX.length).trim();
    note = reason && reason !== "no reason" ? reason : null;
  }
  return {
    kind: "approval_request",
    id: crypto.randomUUID(),
    toolCallId: pending.toolCallId,
    summary: str(pending.input.summary) ?? "",
    body: str(pending.input.body),
    risk: str(pending.input.risk),
    approved,
    note,
  };
}

// A save_plan tool_use is the enforced-approval plan box. The result is the
// registry's gate verdict: not-an-error ⇒ the plan ran ⇒ approved; a "declined"
// error ⇒ denied (with the user's note in parens); any other error (timeout)
// ⇒ left unresolved. summary mirrors the backend save_plan.approval_summary.
function savePlanItem(pending: PendingInteraction, result: RawBlock): ReportingTranscriptItem {
  const content = str(result.content) ?? "";
  let approved: boolean | null;
  let note: string | null = null;
  if (!result.is_error) {
    approved = true;
  } else if (/declined/i.test(content)) {
    approved = false;
    const match = content.match(/\(([^)]*)\)\.?$/);
    note = match && match[1].trim() ? match[1].trim() : null;
  } else {
    approved = null;
  }
  const title = str(pending.input.title);
  return {
    kind: "approval_request",
    id: crypto.randomUUID(),
    toolCallId: pending.toolCallId,
    summary: title ? `Approve this dashboard plan: ${title}?` : "Approve this dashboard plan?",
    body: str(pending.input.plan_markdown),
    risk: null,
    approved,
    note,
  };
}

/**
 * Project the persisted, block-structured transcript into displayable items —
 * the same shape the live store builds, so a reloaded conversation renders
 * identically. Keeps `text` as bubbles; reconstructs the `ask_user` and
 * `request_approval` boxes inline (read-only, showing the recorded
 * answer/decision); and restores the "view dashboard" link on the following
 * agent message. Defensive against malformed input (returns what it can).
 */
export function projectTranscript(messages: unknown): ReportingTranscriptItem[] {
  if (!Array.isArray(messages)) return [];
  const out: ReportingTranscriptItem[] = [];
  const dashboardToolUseIds = new Set<string>();
  const pendingInteractions = new Map<string, PendingInteraction>();
  let pendingArtifact: string | null = null;

  for (const raw of messages as RawMessage[]) {
    if (!raw || typeof raw !== "object") continue;
    const { content } = raw;
    const blocks: RawBlock[] = Array.isArray(content) ? content : [];

    for (const block of blocks) {
      if (block?.type === "tool_use" && typeof block.id === "string" && block.name) {
        if (DASHBOARD_TOOLS.has(block.name)) {
          dashboardToolUseIds.add(block.id);
        } else if (INTERACTION_TOOLS.has(block.name)) {
          pendingInteractions.set(block.id, {
            name: block.name,
            toolCallId: block.id,
            input: block.input && typeof block.input === "object" ? block.input : {},
          });
        }
      }
      if (block?.type === "tool_result" && typeof block.tool_use_id === "string") {
        // Dashboard build → capture the artifact id for the following message.
        if (dashboardToolUseIds.has(block.tool_use_id) && typeof block.content === "string") {
          const match = block.content.match(ARTIFACT_RE);
          if (match) pendingArtifact = match[1];
        }
        // ask_user / request_approval → emit the resolved box inline, in order.
        const pending = pendingInteractions.get(block.tool_use_id);
        if (pending) {
          pendingInteractions.delete(block.tool_use_id);
          if (pending.name === REQUEST_APPROVAL) {
            out.push(approvalRequestItem(pending, block));
          } else if (pending.name === SAVE_PLAN) {
            out.push(savePlanItem(pending, block));
          } else {
            out.push(inputRequestItem(pending, block));
          }
        }
      }
    }

    let text = "";
    if (typeof content === "string") {
      text = content;
    } else {
      text = blocks
        .filter((block) => block?.type === "text" && typeof block.text === "string")
        .map((block) => block.text)
        .join("\n\n");
    }
    text = text.trim();
    if (!text) continue;

    const role = raw.role === "assistant" ? "agent" : "user";
    const item: ReportingTranscriptItem = {
      kind: "message",
      id: crypto.randomUUID(),
      role,
      content: text,
    };
    if (role === "agent" && pendingArtifact) {
      item.dashboardArtifactId = pendingArtifact;
      pendingArtifact = null;
    }
    out.push(item);
  }

  // A dashboard built in the final turn with no trailing agent text — hang it off
  // the last agent message so the link still appears.
  if (pendingArtifact) {
    for (let i = out.length - 1; i >= 0; i--) {
      const item = out[i];
      if (item.kind === "message" && item.role === "agent") {
        item.dashboardArtifactId = pendingArtifact;
        break;
      }
    }
  }

  return out;
}
